import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import LinkExt from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle, Color, FontFamily, FontSize } from "@tiptap/extension-text-style";
import { useEffect, useCallback } from "react";
import {
    Bold, Italic, Underline as UIcon, Strikethrough, List, ListOrdered, Heading1, Heading2, Heading3,
    Quote, Code, Link as LinkIcon, Undo, Redo, Pilcrow, Eraser,
} from "lucide-react";

const TOOLBAR_BTN = "p-2 hover:bg-secondary border border-transparent transition-colors";

function ToolbarBtn({ active, onClick, title, children, testId, disabled }) {
    return (
        <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); if (!disabled) onClick(); }}
            title={title}
            data-testid={testId}
            disabled={disabled}
            className={`${TOOLBAR_BTN} ${active ? "bg-secondary text-ikb border-border" : "text-foreground"} disabled:opacity-40 disabled:cursor-not-allowed`}
        >
            {children}
        </button>
    );
}

// Font choices a recruiter would actually use — limited and curated. We use the
// system font stack first (matches the rest of the app) so JD reads natively
// without imposing a typeface, then offer common serif / mono fallbacks.
const FONT_FAMILIES = [
    { label: "Default", value: "" },
    { label: "Sans serif", value: "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" },
    { label: "Serif", value: "ui-serif, Georgia, Cambria, 'Times New Roman', serif" },
    { label: "Monospace", value: "ui-monospace, 'JetBrains Mono', Menlo, monospace" },
    { label: "Inter", value: "Inter, sans-serif" },
];

// Font sizes via inline style on a `<span style="font-size:...">`. Limited to a
// sensible JD range so users don't blow up text past the design intent.
const FONT_SIZES = [
    { label: "Default", value: "" },
    { label: "12px (small)", value: "12px" },
    { label: "14px (body)", value: "14px" },
    { label: "16px", value: "16px" },
    { label: "18px (lead)", value: "18px" },
    { label: "20px", value: "20px" },
    { label: "24px", value: "24px" },
];

/* Strip the noisy formatting that Word / Google Docs / Outlook paste into the
   document. We keep STRUCTURE (paragraphs, headings, lists, links, bold/italic
   where intentional) but drop every inline style, mso-* attribute, color, and
   font directive — these are what make a pasted JD render as one giant bold
   blob with weird fonts. */
function stripPasteFormatting(html) {
    if (!html) return "";
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
    const root = doc.body.firstChild;
    if (!root) return html;

    // Remove all <meta>, <style>, comments, <o:p> and other Word artifacts.
    root.querySelectorAll("meta, style, link, o\\:p").forEach((n) => n.remove());

    const STRIP_ATTRS = ["style", "class", "color", "face", "size", "bgcolor",
        "lang", "valign", "align", "border", "cellpadding", "cellspacing",
        "width", "height"];

    const walk = (node) => {
        if (!node) return;
        if (node.nodeType === 1) {
            // 1 = ELEMENT_NODE
            const tag = node.nodeName.toLowerCase();
            // Replace <b>/<strong> wrapping ENTIRE content with the bare children
            // when the source was clearly auto-bolded by Word (common when pasting
            // a doc that had Cambria/Calibri body text rendered as bold in IE).
            // Heuristic: if a top-level <b>/<strong>/<span style="font-weight">
            // wraps the WHOLE pasted block, unwrap it.
            // For nested cases, preserve the tag.
            if ((tag === "b" || tag === "strong") && node.parentNode === root && root.childNodes.length === 1) {
                while (node.firstChild) root.appendChild(node.firstChild);
                root.removeChild(node);
                walk(root);
                return;
            }
            // Convert <font> to <span> (deprecated)
            if (tag === "font") {
                const span = doc.createElement("span");
                while (node.firstChild) span.appendChild(node.firstChild);
                node.parentNode.replaceChild(span, node);
                walk(span);
                return;
            }
            // Drop all noisy attributes
            STRIP_ATTRS.forEach((a) => node.removeAttribute(a));
            // Drop MSO / data-* / aria-* artifacts
            Array.from(node.attributes || []).forEach((attr) => {
                if (attr.name.startsWith("mso-") || attr.name.startsWith("data-mce") || attr.name.startsWith("xmlns") || attr.name.startsWith("data-ogsc") || attr.name.startsWith("data-ogsb")) {
                    node.removeAttribute(attr.name);
                }
            });
            Array.from(node.childNodes).forEach(walk);
        }
    };
    walk(root);
    return root.innerHTML;
}

export default function RichTextEditor({ value, onChange, placeholder, testId, minHeight = 240 }) {
    const editor = useEditor({
        extensions: [
            // StarterKit v3 ships with link + underline already — disable them
            // here so we can re-add our own configured versions WITHOUT the
            // "Duplicate extension names" runtime warning.
            StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: false, underline: false }),
            Underline,
            TextStyle,
            FontSize,         // built-in font-size extension (no inline style needed)
            Color,
            FontFamily,
            LinkExt.configure({
                openOnClick: false,
                autolink: true,
                HTMLAttributes: { rel: "noopener noreferrer", target: "_blank", class: "underline text-ikb" },
            }),
            Placeholder.configure({ placeholder: placeholder || "Write the job description…" }),
        ],
        content: value || "",
        editorProps: {
            attributes: {
                class: "tiptap prose prose-sm max-w-none focus:outline-none px-4 py-3",
                "data-testid": testId ? `${testId}-content` : "rt-editor-content",
                style: `min-height:${minHeight}px;`,
            },
            // Clean noisy paste from Word / Docs / Outlook BEFORE tiptap parses it.
            // This is the fix for "pasted JD appears bold" — those sources wrap
            // body text in <b>/<strong> or apply font-weight via inline style.
            transformPastedHTML: (html) => stripPasteFormatting(html),
        },
        onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    });

    // Keep editor in sync if parent rewrites the value (e.g. AI fill)
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        if (value && value !== current) {
            editor.commands.setContent(value || "", false);
        }
    }, [value, editor]);

    const setLink = useCallback(() => {
        if (!editor) return;
        const prev = editor.getAttributes("link").href || "";
        const url = window.prompt("Link URL (leave blank to remove)", prev);
        if (url === null) return;
        if (url === "") return editor.chain().focus().extendMarkRange("link").unsetLink().run();
        const safe = /^https?:\/\//i.test(url) ? url : `https://${url}`;
        editor.chain().focus().extendMarkRange("link").setLink({ href: safe }).run();
    }, [editor]);

    const setFontFamily = useCallback((value) => {
        if (!editor) return;
        if (value === "") editor.chain().focus().unsetFontFamily().run();
        else editor.chain().focus().setFontFamily(value).run();
    }, [editor]);

    const setFontSize = useCallback((value) => {
        if (!editor) return;
        if (value === "") editor.chain().focus().unsetFontSize().run();
        else editor.chain().focus().setFontSize(value).run();
    }, [editor]);

    const currentFontFamily = editor?.getAttributes("textStyle")?.fontFamily || "";
    const currentFontSize = editor?.getAttributes("textStyle")?.fontSize || "";

    if (!editor) return null;

    return (
        <div className="border border-border bg-white" data-testid={testId || "rt-editor"}>
            <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-border bg-secondary/40">
                {/* Block type selector — replaces the H1/H2/H3 + Pilcrow buttons
                    with a single compact dropdown, leaves room for fonts. */}
                <select
                    value={
                        editor.isActive("heading", { level: 1 }) ? "h1" :
                        editor.isActive("heading", { level: 2 }) ? "h2" :
                        editor.isActive("heading", { level: 3 }) ? "h3" : "p"
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                    onChange={(e) => {
                        const v = e.target.value;
                        const chain = editor.chain().focus();
                        if (v === "p") chain.setParagraph().run();
                        else chain.toggleHeading({ level: Number(v.slice(1)) }).run();
                    }}
                    className="text-xs h-8 border border-border bg-white px-1.5 mr-1"
                    data-testid="rt-block-select"
                >
                    <option value="p">Paragraph</option>
                    <option value="h1">Heading 1</option>
                    <option value="h2">Heading 2</option>
                    <option value="h3">Heading 3</option>
                </select>
                <select
                    value={currentFontFamily}
                    onMouseDown={(e) => e.stopPropagation()}
                    onChange={(e) => setFontFamily(e.target.value)}
                    className="text-xs h-8 border border-border bg-white px-1.5 mr-1 max-w-[120px]"
                    data-testid="rt-font-family"
                    title="Font family"
                >
                    {FONT_FAMILIES.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
                </select>
                <select
                    value={currentFontSize}
                    onMouseDown={(e) => e.stopPropagation()}
                    onChange={(e) => setFontSize(e.target.value)}
                    className="text-xs h-8 border border-border bg-white px-1.5 mr-1"
                    data-testid="rt-font-size"
                    title="Font size"
                >
                    {FONT_SIZES.map((s) => <option key={s.label} value={s.value}>{s.label}</option>)}
                </select>
                <span className="w-px h-5 bg-border mx-1" />
                <ToolbarBtn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold (⌘B)" testId="rt-bold"><Bold className="w-4 h-4" /></ToolbarBtn>
                <ToolbarBtn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic (⌘I)" testId="rt-italic"><Italic className="w-4 h-4" /></ToolbarBtn>
                <ToolbarBtn active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline (⌘U)" testId="rt-underline"><UIcon className="w-4 h-4" /></ToolbarBtn>
                <ToolbarBtn active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough" testId="rt-strike"><Strikethrough className="w-4 h-4" /></ToolbarBtn>
                <span className="w-px h-5 bg-border mx-1" />
                <ToolbarBtn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list" testId="rt-ul"><List className="w-4 h-4" /></ToolbarBtn>
                <ToolbarBtn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list" testId="rt-ol"><ListOrdered className="w-4 h-4" /></ToolbarBtn>
                <ToolbarBtn active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Quote"><Quote className="w-4 h-4" /></ToolbarBtn>
                <ToolbarBtn active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Code block"><Code className="w-4 h-4" /></ToolbarBtn>
                <span className="w-px h-5 bg-border mx-1" />
                <ToolbarBtn active={editor.isActive("link")} onClick={setLink} title="Add / edit link" testId="rt-link"><LinkIcon className="w-4 h-4" /></ToolbarBtn>
                <ToolbarBtn
                    onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().setParagraph().run()}
                    title="Clear all formatting (resets to plain paragraph)"
                    testId="rt-clear-format"
                >
                    <Eraser className="w-4 h-4" />
                </ToolbarBtn>
                <span className="w-px h-5 bg-border mx-1" />
                <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} title="Undo" testId="rt-undo"><Undo className="w-4 h-4" /></ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} title="Redo" testId="rt-redo"><Redo className="w-4 h-4" /></ToolbarBtn>
            </div>
            <EditorContent editor={editor} />
        </div>
    );
}

/** Read-only view that safely renders the sanitized HTML stored in `description`. */
export function RichTextView({ html, className = "" }) {
    return (
        <div
            className={`tiptap prose prose-sm sm:prose-base max-w-none ${className}`}
            data-testid="rt-view"
            dangerouslySetInnerHTML={{ __html: html || "" }}
        />
    );
}
